import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import * as L from "lucide-react";

/**
 * TelegramConnectModal — pairing flow for Telegram.
 *
 * UX: user clicks Connect on the Telegram tile → we mint a short pairing
 * code, show a big "open Telegram" deep-link button, and poll `/status`
 * every 2s. The moment the webhook records a successful `/start CODE`
 * from the user in Telegram, status flips connected=true and we
 * auto-close the modal with a success toast.
 */
export default function TelegramConnectModal({ onClose, onDone }) {
  const [loading, setLoading] = useState(true);
  const [pair, setPair] = useState(null); // {code, deep_link, expires_at, bot_username}
  const [error, setError] = useState(null);
  const [polling, setPolling] = useState(false);
  const [copied, setCopied] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.post("/channels/telegram/pair");
        if (cancelled) return;
        setPair(r.data);
        setPolling(true);
      } catch (e) {
        setError(e?.response?.data?.detail || "Couldn't start pairing — try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  // Poll status while the modal is open + we have a fresh code. Uses a local
  // `alive` flag + interval cleanup — no cross-effect refs, so React 18
  // strict-mode's double-invoke of effects doesn't wedge us in a stale-closed
  // "stopped" state.
  useEffect(() => {
    if (!polling) return;
    let alive = true;
    pollRef.current = setInterval(async () => {
      if (!alive) return;
      try {
        const r = await api.get("/channels/telegram/status");
        if (!alive) return;
        if (r.data?.connected) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          toast.success(`Telegram connected as ${r.data.handle || "you"} ✓`);
          onDone?.();
        }
      } catch {
        // silent — network hiccup shouldn't spam toasts
      }
    }, 2500);
    return () => {
      alive = false;
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [polling, onDone]);

  const copyCode = async () => {
    if (!pair?.code) return;
    try {
      await navigator.clipboard.writeText(pair.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast("Copy blocked by your browser. Long-press the code to copy.");
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 fade-up"
      onClick={onClose}
      data-testid="telegram-connect-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-3xl bg-[var(--rm-surface)] rammy-shadow-lg overflow-hidden"
      >
        {/* Header */}
        <div className="p-6 pb-4 flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[var(--rammy-cyan-dark)] font-semibold">Connect Telegram</div>
            <div className="mt-1 font-display text-xl">Pair in 15 seconds.</div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-[var(--rammy-surface-alt)] flex items-center justify-center text-[var(--rammy-text-muted)]"
            aria-label="Close"
            data-testid="telegram-modal-close"
          >
            <L.X size={16}/>
          </button>
        </div>

        {loading && (
          <div className="px-6 pb-6 py-4 flex items-center gap-2 text-sm text-[var(--rammy-text-muted)]">
            <L.Loader2 size={14} className="animate-spin"/> Getting your pairing code…
          </div>
        )}

        {error && (
          <div className="px-6 pb-6 text-sm text-red-600" data-testid="telegram-modal-error">{error}</div>
        )}

        {pair && !error && (
          <div className="px-6 pb-6 space-y-4">
            {/* Step 1 — primary CTA: deep link */}
            <div className="rounded-2xl bg-[var(--rammy-cyan)]/10 border border-[var(--rammy-cyan)]/40 p-4">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[var(--rammy-cyan-dark)] font-semibold mb-2">
                <span className="w-4 h-4 rounded-full bg-[var(--rammy-cyan)] text-[var(--rammy-text)] flex items-center justify-center text-[9px] font-bold">1</span>
                One-tap open
              </div>
              <p className="text-sm text-[var(--rammy-text-sec)] leading-relaxed mb-3">
                Opens the Rammy bot in Telegram with the pairing code pre-filled — just tap the blue Start button.
              </p>
              <a
                href={pair.deep_link}
                target="_blank"
                rel="noreferrer noopener"
                data-testid="telegram-deep-link"
                className="w-full block text-center rounded-full bg-[var(--rm-ink-900)] text-white px-4 py-2.5 text-sm font-medium hover:opacity-90 transition inline-flex items-center justify-center gap-2"
              >
                <L.Send size={14}/> Open @{pair.bot_username}
              </a>
            </div>

            {/* Step 2 — manual fallback */}
            <div className="rounded-2xl border border-[var(--rammy-border)] p-4">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[var(--rammy-text-muted)] font-semibold mb-2">
                <span className="w-4 h-4 rounded-full bg-[var(--rammy-surface-alt)] text-[var(--rammy-text-sec)] flex items-center justify-center text-[9px] font-bold">2</span>
                Or paste manually
              </div>
              <p className="text-xs text-[var(--rammy-text-sec)] leading-relaxed mb-3">
                Message <span className="font-mono text-[var(--rammy-text)]">@{pair.bot_username}</span> and send:
              </p>
              <button
                onClick={copyCode}
                data-testid="telegram-copy-code"
                className="w-full font-mono text-lg text-center rounded-xl bg-[var(--rammy-surface-alt)] hover:bg-[var(--rammy-cyan)]/10 border border-dashed border-[var(--rammy-border)] hover:border-[var(--rammy-cyan)] transition py-3 relative group"
              >
                <span data-testid="telegram-pair-code">/start {pair.code}</span>
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] uppercase tracking-widest text-[var(--rammy-text-muted)] group-hover:text-[var(--rammy-cyan-dark)]">
                  {copied ? "Copied ✓" : "Copy"}
                </span>
              </button>
              <div className="text-[10px] text-[var(--rammy-text-muted)] font-mono mt-2 text-center">
                Code expires {new Date(pair.expires_at).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}
              </div>
            </div>

            {/* Live status */}
            <div className="flex items-center justify-center gap-2 text-xs text-[var(--rammy-text-muted)]" data-testid="telegram-status-indicator">
              <span className="relative flex w-2 h-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-[var(--rammy-cyan)] opacity-75 animate-ping"/>
                <span className="relative inline-flex w-2 h-2 rounded-full bg-[var(--rammy-cyan-dark)]"/>
              </span>
              Waiting for you in Telegram…
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
